<template>
  <div class="en-inner">
    <div class="en-panel-head">
      <h2 class="en-title">Cuaderno de errores</h2>
      <p class="en-sub">
        Se guardan fallos de API (Axios), errores de Vue, ventana (<code>window</code>), rutas y promesas no manejadas.
        Máximo {{ max }} entradas (persisten en <code>sessionStorage</code> hasta vaciar). «Copiar para agente» genera
        Markdown con contexto.
      </p>
      <div v-if="testSessionActive" class="en-session">
        <Tag value="Sesión de prueba" severity="info" />
      </div>
      <div class="en-toolbar">
        <Button label="Iniciar prueba" icon="pi pi-play" size="small" severity="help" @click="onStartTest" />
        <SnatTicketButton
          source-module="kosexa_front_error_notebook"
          :prefill-subject="snatPrefillSubject"
          :prefill-description="snatPrefillDescription"
          kind-default="error"
          label="Agregar ticket SNAT"
          button-class="p-button-sm p-button-help"
        />
        <Button
          label="Copiar para agente"
          icon="pi pi-file-edit"
          size="small"
          :disabled="!entries.length"
          @click="copyForAgent"
        />
        <Button label="Copiar todo" icon="pi pi-copy" size="small" :disabled="!entries.length" @click="copyAll" />
        <Button
          label="Vaciar"
          icon="pi pi-trash"
          severity="danger"
          size="small"
          class="en-toolbar-sp"
          :disabled="!entries.length"
          @click="onClear"
        />
      </div>
    </div>

    <p v-if="!entries.length" class="en-empty">Aún no hay registros. Cuando falle una petición o haya un error en pantalla, aparecerá acá.</p>

    <div v-else class="en-list">
      <article v-for="e in entries" :key="e.id" class="en-item">
        <div class="en-item-top">
          <Tag :value="labelKind(e.kind)" severity="info" />
          <time class="en-time" :datetime="e.ts">{{ e.ts }}</time>
        </div>
        <p class="en-sum">{{ e.summary }}</p>
        <textarea class="en-ta" readonly rows="10" :value="e.body" @focus="selectTa" />
        <div class="en-item-actions">
          <Button label="Copiar" icon="pi pi-copy" size="small" @click="copyOne(e.body)" />
          <Button label="Quitar" icon="pi pi-times" size="small" class="p-button-text" @click="removeOne(e.id)" />
        </div>
      </article>
    </div>

    <p class="en-hint">
      Consola manual:
      <code>window.__kosexaTiendaPushError('título', 'detalle opcional')</code>
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import { buildMarkdownAgentReport } from '@/utils/errorNotebookCapture';
import SnatTicketButton from '@/components/SnatTicketButton.vue';

const MAX = 50;

export default {
  name: 'ErrorNotebookInner',
  components: { Button, Tag, SnatTicketButton },
  data() {
    return {
      max: MAX,
    };
  },
  computed: {
    ...mapGetters('errorNotebook', ['entries', 'testSessionActive']),
    snatPrefillSubject() {
      const e = this.entries?.[this.entries.length - 1];
      const s = e?.summary ? String(e.summary) : '';
      return s ? `KOSEXA Tienda: ${s.slice(0, 180)}` : 'KOSEXA Tienda: incidente desde cuaderno de errores';
    },
    snatPrefillDescription() {
      const e = this.entries?.[this.entries.length - 1];
      if (!e) return '';
      return `${e.body || ''}`.slice(0, 12000);
    },
  },
  methods: {
    labelKind(k) {
      if (k === 'http') return 'HTTP';
      if (k === 'vue') return 'Vue';
      if (k === 'rejection') return 'Promesa';
      if (k === 'window') return 'Window';
      if (k === 'router') return 'Router';
      if (k === 'manual') return 'Manual';
      return k || '—';
    },
    selectTa(ev) {
      ev.target?.select?.();
    },
    async copyOne(text) {
      await this.writeClipboard(text);
    },
    async copyAll() {
      if (!this.entries.length) return;
      const text = this.entries.map((e) => e.body).join('\n\n────────── siguiente registro ──────────\n\n');
      await this.writeClipboard(text);
    },
    onStartTest() {
      this.$store.dispatch('errorNotebook/startTestSession');
    },
    async copyForAgent() {
      if (!this.entries.length) return;
      const text = buildMarkdownAgentReport(this.entries, this.$route);
      await this.writeClipboard(text);
    },
    async writeClipboard(text) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
      } catch {
        /* noop */
      }
    },
    onClear() {
      if (!this.entries.length) return;
      // eslint-disable-next-line no-alert
      if (window.confirm('¿Vaciar todo el cuaderno de errores?')) {
        this.$store.commit('errorNotebook/clear');
      }
    },
    removeOne(id) {
      this.$store.commit('errorNotebook/remove', id);
    },
  },
};
</script>

<style scoped>
.en-inner {
  min-height: 0;
}

.en-panel-head {
  margin-bottom: 1rem;
}

.en-title {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.en-sub {
  margin: 0 0 0.85rem;
  font-size: 0.85rem;
  line-height: 1.45;
  color: #64748b;
}

.en-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.en-toolbar-sp {
  margin-left: auto;
}

.en-session {
  margin-bottom: 0.5rem;
}

.en-hint {
  margin: 1rem 0 0;
  font-size: 0.72rem;
  color: #64748b;
  word-break: break-all;
}

.en-hint code {
  font-size: 0.68rem;
  background: #f1f5f9;
  padding: 0.1rem 0.25rem;
  border-radius: 4px;
}

.en-empty {
  margin: 1.5rem 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.5;
}

.en-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-bottom: 1rem;
}

.en-item {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.75rem 0.85rem;
  background: #f8fafc;
}

.en-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.en-time {
  font-size: 0.72rem;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}

.en-sum {
  margin: 0 0 0.5rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  word-break: break-word;
}

.en-ta {
  width: 100%;
  box-sizing: border-box;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.72rem;
  line-height: 1.4;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 0.5rem;
  resize: vertical;
  min-height: 8rem;
  background: #fff;
  color: #0f172a;
}

.en-item-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}
</style>
